"use client";

import { Loader2 } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { toast } from "sonner";

import { AUDIENCE_HERO, AuthHeroPanel } from "@/components/auth/audience-hero";
import { TeracWordmark } from "@/components/icons/TeracWordmark";
import { signOut } from "@/lib/auth-client";
import { api } from "@/lib/trpc/react";

/** The organization that owns a signed-in researcher's verified work domain. */
export interface JoinableOrganizationSummary {
  organizationId: string;
  organizationName: string;
  requestStatus: "NONE" | "PENDING";
}

/**
 * What a researcher whose verified work email belongs to an organization's
 * registered domain sees in place of the booking form. Their company is
 * already a customer, so a sales call would only end in a second tenant for
 * it: asking that organization's admins is the one way forward. Asking grants
 * nothing; an approval makes them a member, and this page then forwards
 * members to their organization.
 */
export function ResearcherJoinRequest({
  organization,
  signedInAs,
}: {
  organization: JoinableOrganizationSummary;
  signedInAs: string;
}) {
  const hero = AUDIENCE_HERO.researcher;
  const [requested, setRequested] = useState(
    organization.requestStatus === "PENDING",
  );

  const requestToJoin = api.organizationJoinRequests.request.useMutation({
    onSuccess: () => {
      setRequested(true);
    },
    onError: (error) => {
      toast.error("Couldn't send your request", {
        description: error.message,
      });
    },
  });

  const handleLogout = async () => {
    try {
      await signOut();
      // Full-document nav, not router.push: a soft nav keeps the SPA's
      // in-memory state, so Back would restore this authenticated page.
      window.location.replace("/login");
    } catch (error) {
      toast.error("Couldn't log out", {
        description:
          error instanceof Error ? error.message : "Please try again.",
      });
    }
  };

  return (
    <div className="bg-marketing-cream text-foreground dark:bg-background grid min-h-screen grid-cols-1 lg:grid-cols-2">
      <AuthHeroPanel
        audience="researcher"
        headline={hero.headline}
        subtitle={hero.subtitle}
        panelClass={hero.panelClass}
      />

      <div className="relative flex flex-col justify-center px-6 py-[76px] sm:px-10 lg:py-20">
        <Link
          href="/"
          aria-label="Terac home"
          className="absolute top-6 left-6 sm:top-8 sm:left-10 lg:hidden"
        >
          <TeracWordmark className="text-foreground h-9 w-auto" />
        </Link>

        <div
          data-testid="researcher-join-request"
          className="mx-auto flex w-full max-w-[440px] flex-col"
        >
          {requested ? (
            <div role="status">
              <h2 className="text-foreground m-0 text-[27px] leading-[1.15] font-medium tracking-[-0.02em] min-[1024px]:text-[30px] min-[1440px]:text-[34px]">
                Request sent to {organization.organizationName}
              </h2>
              <p className="mt-2 text-[14px] text-[#606060] lg:text-[15px] dark:text-neutral-400">
                Its admins have been notified. You&apos;ll be added to{" "}
                {organization.organizationName} as soon as one of them approves,
                and we&apos;ll email you when you&apos;re in.
              </p>
            </div>
          ) : (
            <>
              <h2 className="text-foreground m-0 text-[27px] leading-[1.15] font-medium tracking-[-0.02em] min-[1024px]:text-[30px] min-[1440px]:text-[34px]">
                {organization.organizationName} is already on Terac
              </h2>
              <p className="mt-2 text-[14px] text-[#606060] lg:text-[15px] dark:text-neutral-400">
                Your work email matches this organization. Ask its admins to add
                you, and you&apos;ll be in as soon as one of them approves.
              </p>
              <button
                type="button"
                onClick={() => {
                  requestToJoin.mutate({
                    organizationId: organization.organizationId,
                  });
                }}
                disabled={requestToJoin.isPending}
                className="bg-marketing-blue hover:bg-marketing-blue-dark border-marketing-blue dark:border-marketing-blue-light mt-6 inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-full border px-4 py-[13px] text-[14.5px] font-medium tracking-[-0.01em] text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60"
              >
                {requestToJoin.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending request…
                  </>
                ) : (
                  `Request to join ${organization.organizationName}`
                )}
              </button>
            </>
          )}

          <p className="mt-6 text-center text-[13px] text-[#606060] dark:text-neutral-400">
            Logged in as {signedInAs}.{" "}
            <button
              type="button"
              onClick={() => {
                void handleLogout();
              }}
              className="text-foreground border-foreground cursor-pointer border-b pb-px font-medium"
            >
              Log out →
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
